import React from "react";
import { BRAND_COLORS, getCloudProviderColor, getCloudProviderGradient } from "../../config/brandColors.js";

/**
 * Card responsiva para mostrar estadísticas de un proveedor de nube
 * Diseño moderno con colores institucionales y completamente responsiva
 */
const CloudProviderCard = ({ 
  name, 
  logo, 
  color, 
  activeAccounts, 
  inactiveAccounts, 
  totalAccounts, 
  totalBudget,
  id 
}) => {
  const activePercentage = totalAccounts > 0 ? (activeAccounts / totalAccounts) * 100 : 0;
  const providerColor = getCloudProviderColor(id);
  const providerGradient = getCloudProviderGradient(id);

  return (
    <div 
      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group relative"
      style={{ boxShadow: BRAND_COLORS.ui.shadowMedium }}
    >
      {/* Header con gradiente del proveedor */}
      <div 
        className="h-3 w-full"
        style={{ background: providerGradient }}
      ></div>
      
      <div className="p-4 sm:p-6">
        {/* Título con logo - Completamente responsivo */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-3 min-w-0 flex-1">
            <div className="flex-shrink-0">
              <img 
                src={logo} 
                alt={`${name} logo`} 
                className="w-6 h-6 sm:w-8 sm:h-8 object-contain"
              />
            </div>
            <div className="min-w-0 flex-1">
              <h3 
                className="text-sm sm:text-lg font-semibold truncate"
                style={{ color: BRAND_COLORS.primary.darkGray }}
                title={name}
              >
                {name}
              </h3>
            </div>
          </div>
          <div 
            className="text-xs sm:text-sm font-medium px-2 py-1 rounded-full flex-shrink-0 text-center"
            style={{ 
              backgroundColor: `${providerColor}15`,
              color: providerColor
            }}
          >
            {totalAccounts} accounts
          </div>
        </div>

        {/* Estadísticas principales - Layout responsivo */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
          <div className="text-center p-2 sm:p-3 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.support.green}10` }}>
            <div 
              className="text-lg sm:text-2xl font-bold mb-1"
              style={{ color: BRAND_COLORS.support.green }}
            >
              {activeAccounts}
            </div>
            <div 
              className="text-xs sm:text-sm font-medium"
              style={{ color: BRAND_COLORS.ui.textSecondary }}
            >
              Active
            </div>
          </div>
          <div className="text-center p-2 sm:p-3 rounded-lg" style={{ backgroundColor: `${BRAND_COLORS.support.darkOrange}10` }}>
            <div 
              className="text-lg sm:text-2xl font-bold mb-1"
              style={{ color: BRAND_COLORS.support.darkOrange }}
            >
              {inactiveAccounts}
            </div>
            <div 
              className="text-xs sm:text-sm font-medium"
              style={{ color: BRAND_COLORS.ui.textSecondary }}
            >
              Inactive
            </div>
          </div>
        </div>

        {/* Barra de progreso */}
        <div className="mb-4">
          <div className="flex justify-between text-xs font-medium mb-2" style={{ color: BRAND_COLORS.ui.textSecondary }}>
            <span>Account Status</span>
            <span>{totalAccounts > 0 ? Math.round(activePercentage) : 0}% Active</span>
          </div>
          <div 
            className="w-full rounded-full h-2 sm:h-3"
            style={{ backgroundColor: BRAND_COLORS.ui.borderLight }}
          >
            <div 
              className="h-2 sm:h-3 rounded-full transition-all duration-500 ease-out shadow-sm"
              style={{ 
                width: totalAccounts > 0 ? `${activePercentage}%` : '0%',
                background: BRAND_COLORS.support.green
              }}
            ></div>
          </div>
        </div>

        {/* Budget mensual */}
        <div 
          className="border-t pt-3 sm:pt-4"
          style={{ borderColor: BRAND_COLORS.ui.borderLight }}
        >
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-1 sm:space-y-0">
            <span 
              className="text-xs sm:text-sm font-medium"
              style={{ color: BRAND_COLORS.ui.textSecondary }}
            >
              Monthly Budget
            </span>
            <div className="flex items-center space-x-1">
              <span 
                className="text-base sm:text-lg font-bold"
                style={{ color: providerColor }}
              >
                ${totalBudget.toLocaleString()}
              </span>
              {totalBudget > 0 && (
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: BRAND_COLORS.support.green }}
                ></div>
              )}
            </div>
          </div>
        </div>

        {/* Hover effect indicator */}
        <div 
          className="absolute bottom-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
          style={{ background: providerGradient }}
        ></div>
      </div>
    </div>
  );
};

export default CloudProviderCard;
